import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Storage } from '@ionic/storage-angular';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin-v8';
import { Observable, BehaviorSubject, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AppSettings } from '../shared/app-settings';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';

/**
 * Response from login endpoint with both access and refresh tokens
 */
export interface TokenPairResponse {
  Token: string;           // Access token (JWT) - short-lived (4 hours)
  RefreshToken: string;    // Refresh token - long-lived (3 days)
  UserLevel: number;
  UserType: number;
  Name: string;
  // ... other fields from AuthenticationResponse
}

/**
 * Response from refresh token endpoint
 */
export interface RefreshTokenResponse {
  AccessToken: string;
  ExpiresAt: string;
}

/**
 * Authentication Service
 * Handles login, token refresh, and logout with secure storage
 * Provides automatic token refresh on expiry (3-day sessions)
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly ACCESS_TOKEN_KEY = 'token';
  private readonly COMPANY_ID_KEY = 'clientid'; // Must match AppSettings.StorageKeys.ClientID
  
  // Observable to track authentication state
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  
  // Flag to prevent multiple simultaneous refresh attempts
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  
  // Flag to prevent multiple simultaneous logout redirects
  private isLoggingOut = false;

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private router: Router
  ) {
    this.initStorage();
  }

  /**
   * Initialize Ionic Storage
   */
  private async initStorage() {
    await this.storage.create();
    
    // Check if user has valid tokens on startup
    const hasAccessToken = await this.hasAccessToken();
    this.isAuthenticatedSubject.next(hasAccessToken);
  }

  /**
   * Check if user has an access token in storage
   */
  private async hasAccessToken(): Promise<boolean> {
    const token = await this.storage.get(this.ACCESS_TOKEN_KEY);
    return !!token;
  }

  /**
   * Get current access token from Ionic Storage
   */
  async getAccessToken(): Promise<string | null> {
    return await this.storage.get(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get refresh token from Secure Storage (native) or localStorage (web)
   */
  private async getRefreshToken(): Promise<string | null> {
    try {
      // Check if running on native platform
      if (Capacitor.isNativePlatform()) {
        const result = await SecureStoragePlugin.get({ key: this.REFRESH_TOKEN_KEY });
        return result.value;
      } else {
        // Fallback to localStorage for web
        return localStorage.getItem(this.REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Store access token in Ionic Storage (OK to lose, auto-refreshes)
   */
  private async storeAccessToken(token: string): Promise<void> {
    await this.storage.set(this.ACCESS_TOKEN_KEY, token);
    // Also store in localStorage for backward compatibility with existing code
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  /**
   * Store refresh token in Secure Storage (native) or localStorage (web)
   */
  private async storeRefreshToken(refreshToken: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // Use secure storage on native platforms
      await SecureStoragePlugin.set({
        key: this.REFRESH_TOKEN_KEY,
        value: refreshToken
      });
    } else {
      // Fallback to localStorage for web
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Get company ID from storage
   */
  private async getCompanyId(): Promise<number> {
    const companyId = await this.storage.get(this.COMPANY_ID_KEY);
    return companyId ? parseInt(companyId, 10) : 0;
  }

  /**
   * Refresh access token using refresh token
   * Returns new access token or null if refresh fails
   */
  async refreshAccessToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      // Wait for ongoing refresh to complete
      return new Promise((resolve, reject) => {
        const sub = this.refreshTokenSubject.subscribe((token) => {
          // Skip the initial null from BehaviorSubject replay
          // Wait for a real emission (token string or 'FAILED' sentinel)
          if (token === null) return;
          sub.unsubscribe();
          if (token === 'REFRESH_FAILED') {
            resolve(null);
          } else {
            resolve(token);
          }
        });
      });
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null); // Reset for new refresh cycle

    try {
      const refreshToken = await this.getRefreshToken();
      const companyId = await this.getCompanyId();

      if (!refreshToken || !companyId) {
        console.warn('Missing refresh token or company ID', 
          { hasRefreshToken: !!refreshToken, companyId });
        this.isRefreshing = false;
        this.refreshTokenSubject.next('REFRESH_FAILED');
        return null;
      }

      console.log('Refreshing access token...');

      // Call refresh endpoint
      const response = await this.http.post<RefreshTokenResponse>(
        `${AppSettings.API_ENDPOINT}/auth/refresh`,
        {
          RefreshToken: refreshToken,
          CompanyID: companyId
        }
      ).toPromise();

      if (response && response.AccessToken) {
        // Store new access token
        await this.storeAccessToken(response.AccessToken);
        
        // Notify waiting requests
        this.refreshTokenSubject.next(response.AccessToken);
        
        this.isRefreshing = false;
        return response.AccessToken;
      }

      this.isRefreshing = false;
      this.refreshTokenSubject.next('REFRESH_FAILED');
      return null;
    } catch (error: any) {
      console.error('Failed to refresh access token', error);
      this.isRefreshing = false;
      this.refreshTokenSubject.next('REFRESH_FAILED');
      
      // Only logout if it's a definitive auth failure (4xx), not a transient error
      const status = error?.status;
      if (status && status >= 400 && status < 500) {
        // Refresh token is rejected by server (expired/revoked) - logout
        await this.logout(false); // false = don't revoke on server since we can't auth
      } else {
        console.warn('Transient error during token refresh, will retry on next request');
      }
      return null;
    }
  }

  /**
   * Login and store both tokens
   * Called by login component after successful authentication
   */
  async storeTokens(accessToken: string, refreshToken: string): Promise<void> {
    await this.storeAccessToken(accessToken);
    await this.storeRefreshToken(refreshToken);
    this.isAuthenticatedSubject.next(true);
  }

  /**
   * Logout - clear tokens from storage
   * @param revokeOnServer Whether to revoke refresh token on server (default true)
   */
  async logout(revokeOnServer: boolean = true): Promise<void> {
    // Prevent multiple simultaneous logout attempts
    if (this.isLoggingOut) {
      console.log('[AUTH] Logout already in progress, skipping');
      return;
    }
    
    this.isLoggingOut = true;
    
    try {
      if (revokeOnServer) {
        const refreshToken = await this.getRefreshToken();
        const companyId = await this.getCompanyId();

        if (refreshToken && companyId) {
          // Revoke refresh token on server
          await this.http.post(
            `${AppSettings.API_ENDPOINT}/auth/logout`,
            {
              RefreshToken: refreshToken,
              CompanyID: companyId,
              LogoutAllDevices: false
            }
          ).toPromise().catch(err => {
            console.warn('Failed to revoke token on server', err);
          });
        }
      }
    } finally {
      // Clear all tokens regardless of server response
      await this.clearTokens();
      
      // Navigate to login page (only if not already there)
      if (!this.router.url.includes('/login')) {
        this.router.navigate(['/login'], { replaceUrl: true });
      }
      
      this.isLoggingOut = false;
    }
  }

  /**
   * Logout from all devices - revokes all refresh tokens for this user
   */
  async logoutAllDevices(username: string): Promise<void> {
    try {
      const companyId = await this.getCompanyId();

      if (username && companyId) {
        await this.http.post(
          `${AppSettings.API_ENDPOINT}/auth/logout`,
          {
            CompanyID: companyId,
            Username: username,
            LogoutAllDevices: true
          }
        ).toPromise().catch(err => {
          console.warn('Failed to revoke all tokens on server', err);
        });
      }
    } finally {
      // Clear local tokens and redirect
      await this.clearTokens();
      this.router.navigate(['/login']);
    }
  }

  /**
   * Clear all tokens from storage
   */
  private async clearTokens(): Promise<void> {
    // Clear from Ionic Storage
    await this.storage.remove(this.ACCESS_TOKEN_KEY);
    
    // Clear from localStorage (backward compatibility and web fallback)
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem('access_token');
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    
    // Clear refresh token from Secure Storage (native only)
    if (Capacitor.isNativePlatform()) {
      try {
        await SecureStoragePlugin.remove({ key: this.REFRESH_TOKEN_KEY });
      } catch (error) {
        console.warn('Error removing refresh token from secure storage', error);
      }
    }
    
    // Update authentication state
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Check if user is authenticated (has access token or valid refresh token)
   * Returns true only if access token exists OR refresh succeeds
   */
  async isLoggedIn(): Promise<boolean> {
    const hasToken = await this.hasAccessToken();
    
    // If no access token, try to refresh
    if (!hasToken) {
      const refreshToken = await this.getRefreshToken();
      if (refreshToken) {
        console.log('No access token found, attempting to refresh...');
        // Try to get new access token
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          console.log('Successfully refreshed access token');
          return true;
        } else {
          console.warn('Failed to refresh token, clearing stale data');
          // Refresh failed - clear any stale data
          await this.clearTokens();
          return false;
        }
      }
      // No refresh token available
      return false;
    }
    
    return true;
  }
}
